// Helper functions for date/time conversion
import React, {useEffect} from "react";
import {LoadLevelDataPoint} from "../../types/performance";

export const stringToDate = (dateStr: string): Date | null => {
    if (!dateStr || !/\d{4}-\d{2}-\d{2}/.test(dateStr)) return null;
    const parts = dateStr.split('-');
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    // Check if date is valid (e.g. not Feb 30th)
    if (date.getFullYear() === Number(parts[0]) &&
        date.getMonth() === Number(parts[1]) - 1 &&
        date.getDate() === Number(parts[2])) {
        return date;
    }
    return null;
};

export const dateToString = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
};

export const stringToTime = (timeStr: string, dateRef: Date | null = new Date()): Date | null => {
    if (!timeStr || !/\d{2}:\d{2}/.test(timeStr)) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        const newDate = dateRef ? new Date(dateRef) : new Date();
        newDate.setHours(hours, minutes, 0, 0);
        return newDate;
    }
    return null;
};

export const timeToString = (date: Date | null): string => {
    if (!date) return '';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

// Custom hook to detect clicks outside a referenced element
export function useDetectOutsideClick(ref: React.RefObject<HTMLElement>, onOutsideClick: () => void, isDropdownOpen: boolean) {
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                onOutsideClick();
            }
        }

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, onOutsideClick, isDropdownOpen]);
}

/**
 * Given an array of objects each with a LoadLevel string,
 * return the most common (mode) LoadLevel, or null if empty.
 */
export function averageLoadLevel(loadLevels: LoadLevelDataPoint[]) {
    if (!Array.isArray(loadLevels) || loadLevels.length === 0) {
        return null;
    }

    // 1. Count occurrences of each LoadLevel
    const freq: Record<string, number> = loadLevels.reduce((counts, item) => {
        const lvl = item.LoadLevel;
        if (lvl in counts) {
            counts[lvl] += 1;
        } else {
            counts[lvl] = 1;
        }
        return counts;
    }, {});

    // 2. Find which level has the highest count
    let mode = null;
    let maxCount = 0;
    for (const [level, count] of Object.entries(freq)) {
        if (count > maxCount) {
            maxCount = count;
            mode = level;
        }
    }

    return mode;
}
